import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import AppContext from "../../context/AppContext";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiEdit3,
  FiList,
  FiSettings,
  FiTrash2,
  FiUpload,
  FiXCircle,
} from "react-icons/fi";
import TicketUploadHistoryPanel from "../../components/call-center/TicketUploadHistoryPanel";
import { useTickets } from "../../hooks/useTickets";
import { TicketPriority } from "../../service/CallCenterApi";
import {
  buildInitialUploadMappings,
  getDuplicateUploadMappings,
  getMissingMandatoryUploadColumns,
  getUploadMappingPayload,
  KEEP_AS_NEW_FIELD,
  readUploadHeaders,
  resolveFieldKey,
} from "../../components/call-center/ticketUploadUtils";
import { getTicketColumnConfig } from "../../service/CallCenterTickets";
import useCallCenterT from "../../hooks/useCallCenterT";
import ConfirmDialog from "../../components/call-center/listing/ConfirmDialog";
import StatusBadge from "../../components/call-center/listing/StatusBadge";
import {
  FILTER_FIELD_CLASS,
  FILTER_LABEL_CLASS,
  LISTING_CARD_CLASS,
  LISTING_CONTAINER_CLASS,
  LISTING_FILTER_CARD_CLASS,
  LISTING_PAGE_CLASS,
  LISTING_PRIMARY_BUTTON_CLASS,
} from "../../components/call-center/listing/listingStyles";

const acceptedTypes = [".csv", ".xls", ".xlsx"];
const BASE_DISABLED_UPLOAD_MAPPING_FIELD_KEYS = new Set(["priority", "status"]);

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const UploadZoneSkeleton = () => (
  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-10">
    <div className="mx-auto h-8 w-8 animate-pulse rounded-full bg-slate-100" />
    <div className="mx-auto mt-3 h-4 w-48 animate-pulse rounded bg-slate-100" />
    <div className="mx-auto mt-2 h-3 w-32 animate-pulse rounded bg-slate-100" />
  </div>
);

const StepBadge = ({ step, label, active, done }) => (
  <div className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold ${
    done
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : active
        ? "border-[#2B7FFF]/30 bg-blue-50 text-[#2B7FFF]"
        : "border-slate-200 bg-white text-slate-400"
  }`}>
    <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${
      done ? "bg-emerald-600 text-white" : active ? "bg-[#2B7FFF] text-white" : "bg-slate-100 text-slate-400"
    }`}>
      {done ? "✓" : step}
    </span>
    {label}
  </div>
);

const CallCenterTicketBulkUploadPage = () => {
  const t = useCallCenterT();
  const { userType } = useContext(AppContext);
  const ticketsApi = useTickets();
  const fileInputRef = useRef(null);

  const [columnConfig, setColumnConfig] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [headerPreview, setHeaderPreview] = useState([]);
  const [uploadMappings, setUploadMappings] = useState({});
  const [missingHeaders, setMissingHeaders] = useState([]);
  const [bulkPriority, setBulkPriority] = useState(TicketPriority.MEDIUM);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [parsingUpload, setParsingUpload] = useState(false);
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const [lastUploadSummary, setLastUploadSummary] = useState(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [openLatestHistory, setOpenLatestHistory] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [reassignExisting, setReassignExisting] = useState(false);

  const canManageAssignment = useMemo(() => {
    const normalized = String(userType || "").toLowerCase();
    return ["super_admin", "admin", "project_admin", "call_admin", "supervisor"].includes(normalized);
  }, [userType]);

  const disabledUploadMappingFieldKeys = useMemo(() => {
    const keys = new Set(BASE_DISABLED_UPLOAD_MAPPING_FIELD_KEYS);
    if (!reassignExisting) {
      keys.add("assignedTo");
    }
    return keys;
  }, [reassignExisting]);

  const mandatoryUploadColumns = useMemo(
    () =>
      columnConfig
        .filter((column) => column.mandatory && column.fieldKey !== "priority")
        .map((column) => column.displayLabel || column.fieldKey),
    [columnConfig]
  );
  const uploadMappingTargets = useMemo(
    () => columnConfig.filter((column) => !disabledUploadMappingFieldKeys.has(column.fieldKey)),
    [columnConfig, disabledUploadMappingFieldKeys]
  );
  const hasAgentMapping = useMemo(
    () =>
      Object.values(uploadMappings || {}).some(
        (fieldKey) => fieldKey === "agentName" || fieldKey === "assignedTo"
      ),
    [uploadMappings]
  );
  const duplicateUploadMappings = useMemo(
    () => getDuplicateUploadMappings(headerPreview, uploadMappings),
    [headerPreview, uploadMappings]
  );

  const isValidated =
    uploadFile &&
    headerPreview.length > 0 &&
    missingHeaders.length === 0 &&
    duplicateUploadMappings.length === 0;
  const hasValidationIssues =
    missingHeaders.length > 0 || duplicateUploadMappings.length > 0;

  const loadColumnConfig = useCallback(async () => {
    setPageLoading(true);
    setPageError("");
    try {
      const columns = await getTicketColumnConfig();
      setColumnConfig(Array.isArray(columns) ? columns : []);
    } catch (error) {
      setPageError(error.message || t("ccToastLoadColumnConfigFailed"));
    } finally {
      setPageLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadColumnConfig();
  }, [loadColumnConfig]);

  const clearSelectedFile = () => {
    setUploadFile(null);
    setHeaderPreview([]);
    setUploadMappings({});
    setMissingHeaders([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClearFile = () => {
    if (!uploadFile) return;
    setConfirmAction("clear");
  };

  const confirmClearFile = () => {
    clearSelectedFile();
    setConfirmAction(null);
  };

  const onFileSelected = async (event) => {
    const selected = event.target.files?.[0];
    setUploadFile(selected || null);
    setHeaderPreview([]);
    setUploadMappings({});
    setMissingHeaders([]);
    setLastUploadSummary(null);
    if (!selected) return;

    const name = selected.name.toLowerCase();
    const isValidType = acceptedTypes.some((type) => name.endsWith(type));
    if (!isValidType) {
      toast.error(t("ccToastInvalidFileType"));
      clearSelectedFile();
      return;
    }

    try {
      setParsingUpload(true);
      const headers = await readUploadHeaders(selected);
      setHeaderPreview(headers);
      const nextMappings = buildInitialUploadMappings(headers, columnConfig);
      setUploadMappings(nextMappings);
      setMissingHeaders(getMissingMandatoryUploadColumns(headers, columnConfig, nextMappings));
    } catch (error) {
      toast.error(error.message || t("ccToastParseFileFailed"));
      clearSelectedFile();
    } finally {
      setParsingUpload(false);
    }
  };

  const handleUploadMappingChange = (header, targetFieldKey) => {
    setUploadMappings((current) => {
      const next = { ...current, [header]: targetFieldKey };
      setMissingHeaders(getMissingMandatoryUploadColumns(headerPreview, columnConfig, next));
      return next;
    });
  };

  const submitBulkUpload = async () => {
    if (!uploadFile) {
      toast.error(t("ccToastNoFileSelected"));
      return;
    }
    if (!bulkPriority) {
      toast.error(t("ccToastSelectBulkPriority"));
      return;
    }
    if (missingHeaders.length > 0) {
      toast.error(t("ccToastHeaderValidationFailed"));
      return;
    }
    if (duplicateUploadMappings.length > 0) {
      toast.error(t("ccToastDuplicateUploadMapping"));
      return;
    }
    if (reassignExisting && !hasAgentMapping) {
      toast.error(t("ccBulkUploadReassignRequiresAgent"));
      return;
    }
    setConfirmAction("submit");
  };

  const confirmSubmitBulkUpload = async () => {
    if (!uploadFile) return;

    try {
      setUploadingBulk(true);
      const result = await ticketsApi.uploadBulk({
        file: uploadFile,
        priority: bulkPriority,
        stopOnError: false,
        columnMappings: getUploadMappingPayload(headerPreview, uploadMappings),
        reassignExisting: canManageAssignment && reassignExisting,
      });
      const processingAsync =
        result?.processingStatus === "PENDING" || result?.processingStatus === "PROCESSING";
      const summary = {
        successCount: result?.successCount ?? 0,
        ignoredCount: result?.ignoredCount ?? 0,
        failedCount: result?.failedCount ?? 0,
        fileName: uploadFile.name,
        processingAsync,
        uploadLogId: result?.uploadLogId ?? null,
      };
      setLastUploadSummary(summary);
      if (processingAsync) {
        toast.success(
          t("ccUploadQueuedToast") ||
            "Upload queued. Track progress in upload history below."
        );
      } else {
        toast.success(
          t("ccUploadSummaryToast")
            .replace("{success}", summary.successCount)
            .replace("{ignored}", summary.ignoredCount)
            .replace("{failed}", summary.failedCount)
        );
      }
      clearSelectedFile();
      setOpenLatestHistory(true);
      setHistoryRefreshKey((value) => value + 1);
      setConfirmAction(null);
    } catch (error) {
      toast.error(error.message || t("ccToastBulkUploadFailed"));
    } finally {
      setUploadingBulk(false);
    }
  };

  const closeConfirmDialog = () => {
    if (uploadingBulk) return;
    setConfirmAction(null);
  };

  return (
    <div className={LISTING_PAGE_CLASS}>
      <div className={LISTING_CONTAINER_CLASS}>
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-[28px] font-bold tracking-tight text-[#111827]">
              {t("ccTicketBulkUploadTitle")}
            </h1>
            <p className="mt-1 text-sm text-[#6b7280]">{t("ccTicketBulkUploadSubtitle")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/call-center/agent-workspace"
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <FiEdit3 className="h-4 w-4" />
              {t("ccMenuAgentWorkspace")}
            </Link>
            <Link
              to="/call-center/tickets"
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <FiList className="h-4 w-4" />
              {t("ccTicketEntryViewTickets")}
            </Link>
            <Link
              to="/call-center/ticket-settings?tab=columns"
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <FiSettings className="h-4 w-4" />
              {t("ccMenuTicketSettingsMerged")}
            </Link>
          </div>
        </header>

        {pageError && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span>{pageError}</span>
            <button type="button" onClick={loadColumnConfig} className="font-semibold underline-offset-2 hover:underline">
              {t("ccRefresh")}
            </button>
          </div>
        )}

        {lastUploadSummary && (
          <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            <div className="flex flex-wrap items-start gap-3">
              <FiCheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold">{t("ccBulkUploadSuccessTitle")}</p>
                <p className="mt-1 text-xs text-emerald-700">
                  {lastUploadSummary.fileName} · {t("ccUploadSummaryToast")
                    .replace("{success}", lastUploadSummary.successCount)
                    .replace("{ignored}", lastUploadSummary.ignoredCount)
                    .replace("{failed}", lastUploadSummary.failedCount)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setLastUploadSummary(null)}
                className="text-emerald-700 hover:text-emerald-900"
                aria-label={t("ccClose")}
              >
                <FiXCircle className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-12">
          <div className="xl:col-span-8">
            <div className={LISTING_CARD_CLASS}>
              <div className="border-b border-slate-100 px-6 py-4">
                <h2 className="text-base font-semibold text-[#111827]">{t("ccBulkImport")}</h2>
                <p className="mt-1 text-xs text-slate-500">{t("ccBulkUploadFormHint")}</p>
              </div>

              <div className="space-y-6 p-6">
                <div className="flex flex-wrap gap-2">
                  <StepBadge step="1" label={t("ccBulkUploadStepFile")} active={!uploadFile} done={Boolean(uploadFile)} />
                  <StepBadge
                    step="2"
                    label={t("ccBulkUploadStepMapping")}
                    active={Boolean(uploadFile) && !isValidated}
                    done={isValidated}
                  />
                  <StepBadge step="3" label={t("ccBulkUploadStepProcess")} active={isValidated} done={false} />
                </div>

                <section className="rounded-xl border border-slate-200 bg-slate-50/50 p-5">
                  <label className={FILTER_LABEL_CLASS}>{t("ccBulkPriority")}</label>
                  <p className="mb-2 text-xs text-slate-500">{t("ccBulkImportPriorityNote")}</p>
                  <select
                    value={bulkPriority}
                    onChange={(event) => setBulkPriority(event.target.value)}
                    disabled={parsingUpload || uploadingBulk || pageLoading}
                    className={`${FILTER_FIELD_CLASS} max-w-xs`}
                  >
                    {Object.values(TicketPriority).map((priority) => (
                      <option key={`bulk-${priority}`} value={priority}>
                        {priority}
                      </option>
                    ))}
                  </select>
                </section>

                {canManageAssignment ? (
                  <section className="rounded-xl border border-slate-200 bg-white p-5">
                    <label className="flex items-start gap-3 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={reassignExisting}
                        onChange={(event) => setReassignExisting(event.target.checked)}
                        disabled={parsingUpload || uploadingBulk || pageLoading}
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#1d4ed8] focus:ring-[#1d4ed8]"
                      />
                      <span>
                        <span className="font-semibold text-slate-800">
                          {t("ccBulkUploadReassignExisting")}
                        </span>
                        <span className="mt-1 block text-xs text-slate-500">
                          {t("ccBulkUploadReassignExistingHint")}
                        </span>
                      </span>
                    </label>
                  </section>
                ) : null}

                <section>
                  {pageLoading ? (
                    <UploadZoneSkeleton />
                  ) : (
                    <div
                      className={`relative rounded-2xl border border-dashed p-8 text-center transition-all ${
                        uploadFile
                          ? "border-[#2B7FFF] bg-blue-50/40"
                          : "border-slate-200 bg-[#f8fafc] hover:border-slate-300"
                      } ${parsingUpload ? "opacity-70" : ""}`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={acceptedTypes.join(",")}
                        onChange={onFileSelected}
                        disabled={parsingUpload || uploadingBulk}
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                      />
                      <div className="pointer-events-none space-y-2">
                        {parsingUpload ? (
                          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-[#2B7FFF] border-t-transparent" />
                        ) : (
                          <FiUpload className="mx-auto h-8 w-8 text-[#2B7FFF]" strokeWidth={2} />
                        )}
                        <div className="text-sm font-medium text-slate-700">
                          {parsingUpload
                            ? t("ccBulkUploadParsing")
                            : uploadFile
                              ? uploadFile.name
                              : t("ccDropFileOrBrowse")}
                        </div>
                        {uploadFile && !parsingUpload && (
                          <div className="text-xs text-slate-500">{formatFileSize(uploadFile.size)}</div>
                        )}
                        <div className="text-[11px] text-slate-400">{t("ccFileTypesCsvXls")}</div>
                      </div>
                    </div>
                  )}
                </section>

                {(headerPreview.length > 0 || missingHeaders.length > 0) && (
                  <section className="space-y-4 rounded-xl border border-slate-200 bg-slate-50/50 p-5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="text-sm font-semibold text-slate-900">{t("ccValidationResult")}</h3>
                      {isValidated ? (
                        <StatusBadge status="COMPLETED" label={t("ccHeadersValidated")} />
                      ) : hasValidationIssues ? (
                        <StatusBadge status="CANCELLED" label={t("ccBulkUploadValidationFailed")} />
                      ) : (
                        <StatusBadge status="SCHEDULED" label={t("ccBulkUploadValidationPending")} />
                      )}
                    </div>

                    {missingHeaders.length > 0 && (
                      <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
                        {t("ccMissingHeaders")} {missingHeaders.join(", ")}
                      </div>
                    )}
                    {duplicateUploadMappings.length > 0 && (
                      <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
                        <div>{t("ccDuplicateUploadMappingTitle")}</div>
                        {duplicateUploadMappings.map((conflict) => (
                          <div key={`dup-${conflict.fieldKey}`}>
                            {conflict.fieldKey}: {conflict.headers.join(", ")}
                          </div>
                        ))}
                      </div>
                    )}
                    {headerPreview.length > 0 && (
                      <div className="break-all rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] leading-relaxed text-slate-500">
                        <span className="font-semibold text-slate-600">{t("ccFoundHeaders")}</span>{" "}
                        {headerPreview.join(" · ")}
                      </div>
                    )}

                    {headerPreview.length > 0 && (
                      <div className="space-y-2">
                        <div className={FILTER_LABEL_CLASS}>{t("ccUploadColumnMapping")}</div>
                        <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                          {headerPreview.map((header) => {
                            const mappedFieldKey = uploadMappings[header] || KEEP_AS_NEW_FIELD;
                            return (
                              <div
                                key={header}
                                className="grid grid-cols-1 items-center gap-2 rounded-xl border border-slate-200 bg-white p-3 md:grid-cols-[minmax(0,1fr)_minmax(200px,240px)]"
                              >
                                <div>
                                  <div className="break-all text-xs font-semibold text-slate-800">{header}</div>
                                  <div className="mt-0.5 text-[11px] text-slate-400">
                                    {mappedFieldKey === KEEP_AS_NEW_FIELD
                                      ? `${t("ccUploadKeepAsNew")} ${resolveFieldKey(header)}`
                                      : `${t("ccUploadMappedTo")} ${mappedFieldKey}`}
                                  </div>
                                </div>
                                <select
                                  value={mappedFieldKey}
                                  onChange={(event) => handleUploadMappingChange(header, event.target.value)}
                                  disabled={parsingUpload || uploadingBulk}
                                  className={FILTER_FIELD_CLASS}
                                >
                                  <option value={KEEP_AS_NEW_FIELD}>
                                    {t("ccUploadKeepAsNew")} {resolveFieldKey(header)}
                                  </option>
                                  {uploadMappingTargets.map((column) => (
                                    <option key={`${header}-${column.fieldKey}`} value={column.fieldKey}>
                                      {column.displayLabel} ({column.fieldKey})
                                    </option>
                                  ))}
                                </select>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </section>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/60 px-6 py-4">
                <p className="text-xs text-slate-500">{t("ccBulkUploadFooterHint")}</p>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={handleClearFile}
                    disabled={!uploadFile || parsingUpload || uploadingBulk}
                    className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <FiTrash2 className="h-4 w-4" />
                    {t("ccBulkUploadClearFile")}
                  </button>
                  <button
                    type="button"
                    onClick={submitBulkUpload}
                    disabled={
                      !uploadFile ||
                      missingHeaders.length > 0 ||
                      duplicateUploadMappings.length > 0 ||
                      parsingUpload ||
                      uploadingBulk ||
                      pageLoading
                    }
                    className={`${LISTING_PRIMARY_BUTTON_CLASS} disabled:cursor-not-allowed disabled:opacity-70`}
                  >
                    {parsingUpload || uploadingBulk ? t("ccProcessing") : t("ccProcessBulkUpload")}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <aside className="space-y-5 xl:col-span-4">
            <div className={LISTING_FILTER_CARD_CLASS}>
              <h3 className="mb-3 text-sm font-semibold text-slate-900">{t("ccBulkUploadRequiredColumns")}</h3>
              {pageLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="h-4 animate-pulse rounded bg-slate-100" />
                  ))}
                </div>
              ) : (
                <ul className="space-y-2">
                  {(mandatoryUploadColumns.length
                    ? mandatoryUploadColumns
                    : ["Account No", "Agent Name", "Mobile Number"]
                  ).map((column) => (
                    <li key={column} className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#2B7FFF]" />
                      {column}
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-3 text-xs leading-relaxed text-slate-500">{t("ccBulkImportRequiredColumns")}</p>
            </div>

            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
              <div className="flex gap-3">
                <FiAlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                <div className="space-y-2 text-xs leading-relaxed text-amber-800/90">
                  <p>{t("ccBulkImportAgentNameNote")}</p>
                  <p>{t("ccBulkImportMultiMobileNote")}</p>
                  <p>{t("ccTicketEntryAccountPlanNote")}</p>
                </div>
              </div>
            </div>
          </aside>
        </div>

        <TicketUploadHistoryPanel
          compact
          compactLimit={5}
          showViewAllLink
          refreshKey={historyRefreshKey}
          openLatestAfterRefresh={openLatestHistory}
          onLatestOpened={() => setOpenLatestHistory(false)}
        />

        <ConfirmDialog
          open={confirmAction === "clear" || confirmAction === "submit"}
          title={t("ccConfirmDialogTitle")}
          message={
            confirmAction === "clear"
              ? t("ccBulkUploadClearConfirm")
              : t("ccBulkUploadSubmitConfirm")
          }
          cancelLabel={t("ccCancel")}
          confirmLabel={
            confirmAction === "submit" ? t("ccProcessBulkUpload") : t("ccConfirmDialogConfirm")
          }
          processingLabel={t("ccProcessing")}
          variant={confirmAction === "clear" ? "warning" : "primary"}
          loading={confirmAction === "submit" && uploadingBulk}
          onConfirm={confirmAction === "clear" ? confirmClearFile : confirmSubmitBulkUpload}
          onClose={closeConfirmDialog}
        />
      </div>
    </div>
  );
};

export default CallCenterTicketBulkUploadPage;
